/**
 * Tiny typed fetch wrapper. Uses the Vite dev proxy in development
 * (which forwards to the unix socket) and the same-origin /api/* in
 * production (which nginx forwards to the unix socket).
 *
 * All response shapes live here as TypeScript interfaces. The proxy
 * doesn't ship types; the SPA is the single source of truth for what
 * each endpoint returns.
 */

import type { ServiceId, ServiceGroup } from '@shared/services';

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'same-origin',
    ...(body !== undefined
      ? {
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      : {}),
  });
  const text = await res.text();
  const parsed: unknown = text ? safeJsonParse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, `HTTP ${res.status} on ${method} ${path}`, parsed);
  }
  return parsed as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ---- Response shapes ----

export interface ProxmoxNode {
  online: boolean;
  error?: string | null;
  lastUpdated: string | null;
  cpu?: {
    percent: number;
    iowaitPercent: number;
    model: string;
    cores: number;
    loadAverage: number;
  };
  memory?: { used: number; total: number; free: number; percent: number };
  rootfs?: { used: number; total: number; free: number; percent: number };
  uptime?: number;
  kernel?: string;
  pveVersion?: string;
}

export interface ProxmoxContainer {
  vmid: number;
  name: string;
  status: 'running' | 'stopped' | 'paused';
  cpu: number;
  memUsed: number;
  memTotal: number;
  uptime: number;
  netIn: number;
  netOut: number;
}

export interface ProxmoxVm {
  vmid: number;
  name: string;
  status: 'running' | 'stopped' | 'paused';
  cpu: number;
  memUsed: number;
  memTotal: number;
  uptime: number;
}

export interface ProxmoxStorage {
  storage: string;
  type: string;
  used: number;
  total: number;
  avail: number;
  percent: number;
  active: boolean;
}

export interface SystemMetricsPoint {
  timestamp: string;
  cpu: number;
  io_delay: number;
  load_average: number;
  ram_used: number;
  ram_total: number;
}

/**
 * Raw `sensors -j` output. Adapter names are vendor-specific
 * (k10temp-pci-XXXX, coretemp-isa-XXXX, etc); each adapter has a
 * dict of label → { tempN_input: number (millideg C), ... }.
 */
export type SensorsData = Record<string, Record<string, Record<string, number>>>;

export interface ServiceHealth {
  serviceId: ServiceId;
  status: 'healthy' | 'unhealthy' | 'unknown';
  lxcCheckedAt: string | null;
  httpCheckedAt: string | null;
  lastError: string | null;
}

export interface ServiceEntry {
  id: ServiceId;
  displayName: string;
  group: ServiceGroup;
  vmid: number;
  ip: string;
  webUrl: string | null;
  health: ServiceHealth;
}

export interface AdGuardStats {
  totalQueries: number;
  blockedQueries: number;
  blockedPercent: number;
  topBlocked: Array<{ domain: string; count: number }>;
  numRules: number;
  uptime: number;
  version: string;
  protectionEnabled: boolean;
  at: string;
}

export interface QBTorrent {
  hash: string;
  name: string;
  state: string;
  progress: number;
  size: number;
  downloaded: number;
  uploaded: number;
  dlspeed: number;
  upspeed: number;
  eta: number;
  ratio: number;
  category: string;
}

export interface QBittorrentSnapshot {
  torrents: QBTorrent[];
  totals: {
    alltimeDl: number;
    alltimeUl: number;
    globalDlspeed: number;
    globalUpspeed: number;
    freeSpaceOnDisk: number;
  };
  at: string;
  version: string;
}

export interface JellyfinSession {
  id: string;
  userName: string;
  clientName: string;
  deviceName: string;
  nowPlayingItem?: {
    Name: string;
    SeriesName?: string;
    Type: string;
  };
  playState?: { PositionTicks: number; IsPaused: boolean };
  transcodingInfo?: {
    IsVideoDirect: boolean;
    AudioCodec: string;
    VideoCodec: string;
    Bitrate: number;
  };
}

export interface JellyfinStats {
  activeSessions: JellyfinSession[];
  counts: {
    movies: number;
    series: number;
    episodes: number;
    songs: number;
    albums: number;
  };
  serverName: string;
  version: string;
  at: string;
}

export interface PelicanServer {
  identifier: string;
  uuid: string;
  name: string;
  description?: string;
  status: 'running' | 'starting' | 'stopping' | 'offline' | 'unknown';
  isSuspended: boolean;
  resources: { memoryMb: number; cpuPercent: number; diskMb: number };
  limits: { memoryMb: number; cpuPercent: number; diskMb: number };
  node?: string;
}

export interface GiteaRepo {
  id: number;
  fullName: string;
  name: string;
  description: string;
  private: boolean;
  fork: boolean;
  stars: number;
  forks: number;
  openIssues: number;
  sizeKb: number;
  defaultBranch: string;
  updatedAt: string;
  language: string | null;
  htmlUrl: string;
}

export interface GiteaStats {
  version: string;
  totalUsers: number | null;
  totalOrgs: number | null;
  totalRepos: number | null;
  recentRepos: GiteaRepo[];
  at: string;
}

export interface PelicanStats {
  servers: PelicanServer[];
  totalRamMb: number;
  totalCpuPercent: number;
  totalDiskMb: number;
  totalServers: number;
  runningServers: number;
  at: string;
}

export interface PveBackup {
  vmid: number | null;
  volid: string;
  size: number;
  ctime: number;
  format: 'vzdump' | 'other';
}

export interface ProxyEvent {
  id: number;
  timestamp: string;
  kind: 'service' | 'lxc' | 'sensor' | 'alert' | 'pve' | 'system';
  subject: string;
  level: 'info' | 'warn' | 'error';
  message: string;
  metadata: string | null;
}

export interface ServiceHistoryEvent {
  id: number;
  timestamp: string;
  /** Raw message from the events table. */
  message: string;
  level: ProxyEvent['level'];
  /** Parsed from metadata. */
  from: string | null;
  to: string | null;
  lastError: string | null;
}

export interface ServiceHistory {
  serviceId: string;
  window: string;
  events: ServiceHistoryEvent[];
  /** Computed client-side. */
  currentState: 'healthy' | 'unhealthy' | 'unknown';
  currentSince: string | null;
  /** Total transitions in the window. */
  transitionCount: number;
}

// ---- Endpoints ----

export const endpoints = {
  health: () => api.get<{ status: 'ok'; service: string; now: string }>('/health'),

  proxmox: {
    node: () => api.get<ProxmoxNode>('/proxmox/node'),
    lxc: () => api.get<{ lastUpdated: string | null; containers: ProxmoxContainer[] }>('/proxmox/lxc'),
    qemu: () => api.get<{ lastUpdated: string | null; vms: ProxmoxVm[] }>('/proxmox/qemu'),
    storage: () => api.get<{ lastUpdated: string | null; storage: ProxmoxStorage[] }>('/proxmox/storage'),
    metrics: (window: '15m' | '1h' | '6h' | '24h' | '7d' = '1h') =>
      api.get<{ window: string; points: SystemMetricsPoint[] }>(`/proxmox/metrics?window=${window}`),
    lxcMetrics: (vmid: number, window: '15m' | '1h' | '6h' | '24h' | '7d' = '1h') =>
      api.get<{ window: string; vmid: number; points: SystemMetricsPoint[] }>(
        `/proxmox/lxc-metrics?vmid=${vmid}&window=${window}`,
      ),
    sensors: () =>
      api.get<{ lastUpdated: string | null; sensors: SensorsData | null }>('/proxmox/sensors'),
  },

  services: {
    list: () => api.get<{ lastUpdated: string | null; services: ServiceEntry[] }>('/services'),
    one: (id: ServiceId) => api.get<ServiceEntry>(`/services/${id}`),
  },

  events: {
    list: (limit = 100) =>
      api.get<{ events: ProxyEvent[] }>(`/events?limit=${limit}`),
  },

  serviceHistory: (id: ServiceId, window: '24h' | '7d' = '24h') =>
    api.get<ServiceHistory>(`/services/${id}/history?window=${window}`),

  backups: () =>
    api.get<{ lastUpdated: string | null; backups: PveBackup[] }>('/proxmox/backups'),

  control: {
    lxc: (vmid: number, action: 'start' | 'stop' | 'reboot' | 'shutdown' | 'suspend' | 'resume', reason?: string) =>
      api.post<{ ok: boolean; vmid: number; action: string; reason: string; user: string }>(
        `/proxmox/lxc/${vmid}/${action}`,
        reason ? { reason } : undefined,
      ),
  },

  integrations: {
    adguard: () =>
      api.get<{ lastUpdated: string | null; adguard: AdGuardStats | null }>('/services/adguard/stats'),
    qbittorrent: () =>
      api.get<{ lastUpdated: string | null; qbittorrent: QBittorrentSnapshot | null }>(
        '/services/qbittorrent/snapshot',
      ),
    jellyfin: () =>
      api.get<{ lastUpdated: string | null; jellyfin: JellyfinStats | null }>(
        '/services/jellyfin/stats',
      ),
    gitea: () =>
      api.get<{ lastUpdated: string | null; gitea: GiteaStats | null }>(
        '/services/gitea/stats',
      ),
    pelican: {
      stats: () =>
        api.get<{ lastUpdated: string | null; pelican: PelicanStats | null }>('/services/pelican/stats'),
      server: (id: string) =>
        api.get<{ server: unknown }>(`/services/pelican/server/${id}`),
      power: (id: string, signal: 'start' | 'stop' | 'restart' | 'kill') =>
        api.post<{ ok: boolean; id: string; signal: string }>(
          `/services/pelican/server/${id}/power`,
          { signal },
        ),
      files: (id: string, path = '/') =>
        api.get<{ path: string; items: unknown[]; available?: boolean }>(
          `/services/pelican/server/${id}/files?path=${encodeURIComponent(path)}`,
        ),
    },
  },
};
